import type { CaminhoConstruido } from '@/modulos/acesso/sprint';
import { abreATela } from '@/modulos/acesso/telas';

/**
 * As seções do menu que a pessoa abre — e a regra é uma só: TELA NO MENU =
 * TELA QUE O PERFIL PODE USAR.
 *
 * Duas condições, as duas obrigatórias:
 *  - a pessoa LÊ a tela (`LEITURA_DA_TELA`, a mesma licença que a rota guarda
 *    pelo `comLeitura`). Com a sessão em trânsito (`null`), a consulta aparece,
 *    para o menu não piscar vazio;
 *  - quando a tela INTEIRA é uma ação (`exige`, como "Cadastrar equipamento"),
 *    a pessoa tem a licença da ação — e, com a sessão em trânsito, não: a ação
 *    não pisca para quem não pode.
 *
 * Mora fora de `Navegacao.tsx` porque o arquivo do componente só exporta
 * componente (o recarregamento rápido do Vite pede isso).
 */
export function secoesQueAbre<T extends { readonly para: CaminhoConstruido; readonly exige?: string }>(
  secoes: readonly T[],
  permissoes: readonly string[] | null,
  /** O acesso cobre só parte da empresa: as telas da empresa inteira saem (`acesso/alcance.ts`). */
  alcanceParcial = false,
): readonly T[] {
  return secoes.filter(
    (secao) =>
      abreATela(secao.para, permissoes, alcanceParcial) &&
      (secao.exige === undefined || (permissoes !== null && permissoes.includes(secao.exige))),
  );
}

/**
 * Os módulos do menu, recortados pelas telas que a pessoa abre.
 *
 * O módulo construído aparece quando AO MENOS UMA tela dele abre, e o item
 * leva à primeira delas — o operador lê a inspeção e não a fila de O.S.: o
 * item "Ordens de serviço" aparece e leva a `/inspecao`, em vez de sumir com a
 * tela que ele usa (achado 18 dos robôs, 20/10/2026). Sem tela nenhuma que
 * abra, o módulo sai do menu — em vez de levar a "Você não tem acesso a esta
 * tela". O módulo ainda não construído (sem caminho) continua à vista, com a
 * nota de não construído, como sempre.
 */
export function modulosDoMenu<
  S extends { readonly para: CaminhoConstruido; readonly exige?: string },
  M extends { readonly para?: CaminhoConstruido; readonly secoes?: readonly S[] },
>(modulos: readonly M[], permissoes: readonly string[] | null, alcanceParcial = false): readonly M[] {
  const recortados: M[] = [];
  for (const modulo of modulos) {
    if (modulo.para === undefined) {
      recortados.push(modulo);
      continue;
    }
    if (modulo.secoes === undefined) {
      if (abreATela(modulo.para, permissoes, alcanceParcial)) recortados.push(modulo);
      continue;
    }
    const secoes = secoesQueAbre(modulo.secoes, permissoes, alcanceParcial);
    const primeira = secoes[0];
    if (primeira === undefined) continue;
    recortados.push({ ...modulo, para: primeira.para, secoes });
  }
  return recortados;
}
